import { PerformanceReviewerStrategiesStrategyEnum } from 'generated/resources/datatable.graphql'
import {
  PerformanceScoreConfigurationFragment,
  PerformanceSectionWeight,
} from 'generated/resources/graphql'
import { keyBy, mapValues } from 'lodash'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'

import { type MappedStrategyQuestionnaire } from 'modules/performance_process/hooks/useMappedQuestionnaires'
import { isSection } from 'modules/questionnaires/lib/helpers'
import type { Section } from 'modules/questionnaires/lib/types'

const canSectionBeWeighted = (section: Section): boolean => {
  return section.questions.some(
    (question) => question.answerType === 'rating' && question.mandatory
  )
}

/**
 * Transforms score configuration data into a lookup object keyed by reviewer strategy.
 * Extracts and normalizes strategy configurations for easier access removing the typename of graphql.
 */
export const configurationByStrategy = (
  scoreConfiguration?: PerformanceScoreConfigurationFragment | null
) => {
  return keyBy(
    (scoreConfiguration?.strategyConfigurations || []).map((config) => ({
      producesScore: config.producesScore,
      reviewerStrategy: config.reviewerStrategy,
      weight: config.weight,
      sectionWeights: config.sectionWeights.map((sw) => ({
        sectionId: sw.sectionId,
        weight: sw.weight,
      })),
    })),
    'reviewerStrategy'
  )
}

type StrategyWeightsProps = {
  strategy: PerformanceReviewerStrategiesStrategyEnum
  scoreConfiguration?: PerformanceScoreConfigurationFragment | null
}

/**
 * Hook for getting the weights for a given strategy.

 * Use this hook when you need to:
 * - Check if weight controls should be displayed in the UI for a given strategy
 * - Access existing section weights for a specific reviewer strategy
 * - Validate if weighting is available for the current strategy
 *
 */
export const useStrategyWeights = ({ strategy, scoreConfiguration }: StrategyWeightsProps) => {
  const hasCustomWeights = scoreConfiguration?.mode === 'custom_weights'
  const strategyConfiguration = keyBy(
    scoreConfiguration?.strategyConfigurations || [],
    'reviewerStrategy'
  )[strategy]
  const producesScore = strategyConfiguration?.producesScore ?? false

  const strategySectionWeightsById: Record<string, number> = mapValues(
    keyBy(strategyConfiguration?.sectionWeights || [], 'sectionId'),
    'weight'
  )

  const isSectionWeightVisible = hasCustomWeights && producesScore

  return {
    hasCustomWeights,
    isSectionWeightVisible,
    isStrategyWeightable: true,
    weightsBySectionId: strategySectionWeightsById,
  }
}

type SectionWeightsProps = {
  questionnaire: MappedStrategyQuestionnaire
  weights?: PerformanceSectionWeight[]
}
/**
 * Hook for getting the weightable sections of a questionnaire and the section weights.

 * Use this hook when you need to:
 * - Check if weight controls should be displayed in the UI for a given strategy.
 * - Access section weights for a specific reviewer strategy prefilled with the questionniare sections.
 * - Access weightable sections from the questionnaire.
 *
 */
export const useSectionWeights = ({ questionnaire, weights = [] }: SectionWeightsProps) => {
  const allElements = [...questionnaire.general, ...questionnaire.competencies]
  const sections: Section[] = allElements.filter(isSection)
  const weightableSectionById = mapValues(keyBy(sections, 'uuid'), canSectionBeWeighted)

  const isSectionWeightable = useUnsafeCallback(
    (sectionId: string): boolean => {
      return weightableSectionById[sectionId] ?? false
    },
    [weightableSectionById]
  )

  const weightsLookup = keyBy(weights, 'sectionId')
  const sectionWeights = mapValues(
    weightableSectionById,
    (value, sectionId) => weightsLookup[sectionId]?.weight || 0
  )

  return {
    isSectionWeightable,
    sectionWeights,
    sections,
  }
}
